import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { fileUrl } from "../lib/api";
import { Menu, X } from "lucide-react";
import LegendsMark from "./LegendsMark";

const Logo = ({ dark = false, logoPath, logoPathDark, loading = false }) => {
    const color = dark ? "text-white" : "text-[#002951]";
    // Prefer uploaded logo (use dark variant on light bg if provided)
    const imageSrc = !dark ? (logoPathDark || logoPath) : logoPath;
    return (
        <Link to="/" className={`flex min-w-0 items-center gap-3 ${color}`} data-testid="logo-link">
            {imageSrc ? (
                <img
                    src={fileUrl(imageSrc)}
                    alt="Legends"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width="230"
                    height="56"
                    className="h-11 max-w-[185px] object-contain sm:h-12 sm:max-w-[220px] lg:h-14 lg:max-w-[250px]"
                    data-testid="logo-image"
                />
            ) : loading ? (
                <span
                    aria-hidden="true"
                    className="block h-11 w-[185px] shrink-0 sm:h-12 sm:w-[220px] lg:h-14 lg:w-[250px]"
                    data-testid="logo-placeholder"
                />
            ) : (
                <>
                    <span className="heading truncate text-xl font-black tracking-tighter leading-none sm:text-2xl md:text-3xl">
                        LEGENDS
                    </span>
                    <LegendsMark size={26} color="#0060A7" data-testid="logo-mark" />
                </>
            )}
        </Link>
    );
};

const navItems = [
    { to: "/", key: "nav_home" },
    { to: "/welcome", key: "nav_welcome" },
    { to: "/about", key: "nav_about" },
    { to: "/services", key: "nav_services" },
    { to: "/work", key: "nav_works" },
    { to: "/news", key: "nav_news" },
    { to: "/global-reach", key: "nav_global" },
    { to: "/contact", key: "nav_contact" },
];

export default function Navbar() {
    const { t, lang, toggle } = useI18n();
    const { settings, settingsLoading } = useSiteSettings();
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!open) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const items = navItems;
    useEffect(() => {
        const logoPath = settings.logo_path_dark || settings.logo_path;
        if (!logoPath) return;

        const href = fileUrl(logoPath);
        if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return;

        const preload = document.createElement("link");
        preload.rel = "preload";
        preload.as = "image";
        preload.href = href;
        document.head.appendChild(preload);
    }, [settings.logo_path, settings.logo_path_dark]);

    const navTextClass = "text-[#002951]";
    const controlClass = "border-[#002951]/15 hover:border-[#0060A7] hover:text-[#0060A7] text-[#002951]";
    const mobileMenu = (
        <div
            className="fixed inset-0 z-[100] flex h-[100dvh] flex-col bg-[#002951] text-white"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-5 pt-[env(safe-area-inset-top)] sm:h-20 sm:px-6">
                <Logo dark logoPath={settings.logo_path} logoPathDark={settings.logo_path_dark} loading={settingsLoading} />
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="close"
                    className="grid h-11 w-11 shrink-0 place-items-center text-white"
                >
                    <X size={28} />
                </button>
            </div>
            <nav className="flex min-h-0 flex-1 flex-col items-start gap-4 overflow-y-auto px-6 py-8 sm:gap-6 sm:px-8 sm:py-12">
                {items.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.to === "/"}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                                `heading max-w-full break-words text-3xl leading-tight sm:text-4xl transition-colors hover:text-[#2BB0D4] ${
                                isActive ? "text-[#81D3EB]" : "text-white"
                            }`
                        }
                        data-testid={`mobile-nav-${n.key}`}
                    >
                        {t(n.key)}
                    </NavLink>
                ))}
                <button
                    onClick={toggle}
                    className="mt-3 min-h-11 border border-white/30 px-4 py-2 text-sm uppercase tracking-[0.24em] transition-colors hover:bg-white hover:text-[#002951] sm:mt-6"
                    data-testid="mobile-language-toggle"
                >
                    {lang === "ar" ? "EN" : "AR"}
                </button>
            </nav>
        </div>
    );

    return (
        <header
            data-testid="site-navbar"
            className="fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-white/90 backdrop-blur-xl border-b border-black/5"
        >
            <div className="max-w-[1440px] mx-auto h-20 px-5 flex items-center justify-between sm:h-24 sm:px-6 lg:px-12">
                <Logo logoPath={settings.logo_path} logoPathDark={settings.logo_path_dark} loading={settingsLoading} />
                <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
                    {items.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.key}`}
                            className={({ isActive }) =>
                                `legends-link text-sm tracking-wide ${
                                    isActive ? "text-[#0060A7] font-semibold" : navTextClass
                                }`
                            }
                        >
                            {t(n.key)}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <button
                        onClick={toggle}
                        data-testid="language-toggle-button"
                        className={`hidden md:inline-flex items-center gap-1 px-3 py-2 border text-xs uppercase tracking-[0.2em] transition-colors ${controlClass}`}
                    >
                        {lang === "ar" ? "EN" : "ع"}
                    </button>
                    <Link
                        to="/contact"
                        data-testid="navbar-cta"
                        className="hidden md:inline-flex items-center gap-2 bg-[#002951] hover:bg-[#0060A7] text-white text-sm px-5 py-3 transition-colors"
                    >
                        <LegendsMark size={14} color="#ffffff" />
                        {t("cta_contact")}
                    </Link>
                    <button
                        type="button"
                        className={`grid h-11 w-11 shrink-0 place-items-center lg:hidden ${navTextClass}`}
                        onClick={() => setOpen((s) => !s)}
                        aria-expanded={open}
                        data-testid="open-mobile-menu"
                        aria-label="menu"
                    >
                        <Menu size={28} />
                    </button>
                </div>
            </div>

            {open && createPortal(mobileMenu, document.body)}
            {false && open && (
                <div className="fixed inset-0 bg-[#002951] text-white z-[60] flex flex-col" data-testid="mobile-menu">
                    <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
                        <Logo dark logoPath={settings.logo_path} logoPathDark={settings.logo_path_dark} loading={settingsLoading} />
                        <button type="button" onClick={() => setOpen(false)} aria-label="close">
                            <X size={28} />
                        </button>
                    </div>
                    <nav className="flex-1 flex flex-col items-start px-8 py-12 gap-6 overflow-y-auto">
                        {items.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                onClick={() => setOpen(false)}
                                className="heading text-4xl tracking-tight hover:text-[#0060A7] transition-colors"
                                data-testid={`mobile-nav-${n.key}`}
                            >
                                {t(n.key)}
                            </NavLink>
                        ))}
                        <button
                            onClick={toggle}
                            className="mt-6 text-sm uppercase tracking-[0.3em] border border-white/30 px-4 py-2 hover:bg-white hover:text-[#002951] transition-colors"
                            data-testid="mobile-language-toggle"
                        >
                            {lang === "ar" ? "Switch to English" : "التبديل للعربية"}
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
}
